import { useEffect, useState, useCallback } from "react";
import { Plug, Plus, Pencil, Trash2, Link2, Check, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const BLANK = { name: "", category: "", url: "", logo: "", connection_method: "oauth" };

export function AdminProviders() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [testing, setTesting] = useState({});
  const load = useCallback(() => { api.get("/admin/providers").then((r) => setRows(r.data.providers)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { name: edit.name, category: edit.category, url: edit.url || "", logo: edit.logo || "", connection_method: edit.connection_method || "oauth" };
      if (edit.provider_id) { await api.patch(`/admin/providers/${edit.provider_id}`, payload); toast.success("Provider updated"); }
      else { await api.post("/admin/providers", payload); toast.success("Provider added"); }
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete provider "${p.name}"?`)) return;
    try { await api.delete(`/admin/providers/${p.provider_id}`); toast.success("Provider deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const testLink = async (p) => {
    if (!p.url) { toast.error("This provider has no URL to test"); return; }
    setTesting((t) => ({ ...t, [p.provider_id]: "loading" }));
    try {
      const r = await api.post("/control/providers/test-link", { url: p.url });
      setTesting((t) => ({ ...t, [p.provider_id]: r.data.ok ? "ok" : "fail" }));
      r.data.ok ? toast.success(r.data.detail) : toast.error(r.data.detail);
    } catch (e) { setTesting((t) => ({ ...t, [p.provider_id]: "fail" })); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-providers-page">
      <PageHeader title="Service Providers" icon={Plug}
        subtitle="Manage the provider links shown across the platform. Add, edit, remove and test that each link is reachable."
        actions={<button onClick={() => setEdit({ ...BLANK })} data-testid="new-provider-btn" className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-4 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Add Provider</button>} />

      <div className="card-soft overflow-x-auto p-0">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="text-left text-xs uppercase text-slate-400"><tr>
            <th className="p-3">Provider</th><th className="p-3">Category</th><th className="p-3">Link</th><th className="p-3">Method</th><th className="p-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.provider_id} className="border-t border-slate-100" data-testid={`provider-row-${p.provider_id}`}>
                <td className="p-3">
                  <span className="flex items-center gap-2 font-semibold text-slate-800">
                    {p.logo ? <img src={p.logo} alt="" className="h-6 w-6 rounded object-contain" /> : <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 text-[10px] text-slate-400">{(p.name || "?")[0]}</span>}
                    {p.name}
                  </span>
                </td>
                <td className="p-3 text-slate-500">{p.category}</td>
                <td className="p-3 max-w-[220px] truncate text-blue-600">{p.url ? <a href={p.url} target="_blank" rel="noreferrer noopener noreferrer" className="underline">{p.url}</a> : <span className="text-slate-300">—</span>}</td>
                <td className="p-3 text-slate-500">{p.connection_method}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => testLink(p)} data-testid={`test-link-${p.provider_id}`} className="mr-1 inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-300">
                    {testing[p.provider_id] === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : testing[p.provider_id] === "ok" ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : testing[p.provider_id] === "fail" ? <X className="h-3.5 w-3.5 text-rose-500" /> : <Link2 className="h-3.5 w-3.5" />} Test link
                  </button>
                  <button onClick={() => setEdit({ ...p })} data-testid={`edit-provider-${p.provider_id}`} className="rounded-lg p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-500"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(p)} data-testid={`delete-provider-${p.provider_id}`} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">No providers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="provider-dialog">
          <DialogHeader><DialogTitle>{edit?.provider_id ? `Edit ${edit.name}` : "Add provider"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><label className="text-xs font-semibold text-slate-600">Name</label><input value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="provider-name" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
              <div><label className="text-xs font-semibold text-slate-600">Category</label><input value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })} placeholder="e.g. Payments, Shipping, Email" data-testid="provider-category" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
              <div><label className="text-xs font-semibold text-slate-600">URL</label><input value={edit.url || ""} onChange={(e) => setEdit({ ...edit, url: e.target.value })} placeholder="https://…" data-testid="provider-url" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
              <div><label className="text-xs font-semibold text-slate-600">Logo URL (optional)</label><input value={edit.logo || ""} onChange={(e) => setEdit({ ...edit, logo: e.target.value })} data-testid="provider-logo" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
              <div><label className="text-xs font-semibold text-slate-600">Connection method</label><select value={edit.connection_method || "oauth"} onChange={(e) => setEdit({ ...edit, connection_method: e.target.value })} data-testid="provider-method" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"><option value="oauth">OAuth</option><option value="api_key">API key</option><option value="link">Link only</option><option value="manual">Manual</option></select></div>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setEdit(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600">Cancel</button>
                <button onClick={save} data-testid="provider-save" className="rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white">{edit.provider_id ? "Save changes" : "Add provider"}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
